// src/features/analytics/AnalyticsApi.ts
//
// Static API class over the shared webApi singleton for the /v1/analytics
// gateway (paths WITHOUT the /v1/ prefix — buildUrl adds it). Only allowlisted
// params are sent (from, to, metric, interval, limit, finalizedOnly); the
// browser NEVER sends parentCid (it would be dropped server-side anyway).

import { webApi, type BlobResponse } from '@/shared/api/WebApi';
import type { UtcRange } from '@/shared/utils/utcRange';
import type {
  AnalyticsDimension,
  AnalyticsInterval,
  AnalyticsMetric,
  AnalyticsScopeKind,
} from '@/lib/constants/analytics';
import type {
  AnalyticsEnvelope,
  AnalyticsMeta,
  AudienceData,
  ConcurrencyData,
  HeatmapData,
  LiveByMediaData,
  LiveData,
  OverviewData,
  SubaccountsData,
  TopContentData,
  TransferData,
} from './types';

/** Resolved view scope: account = selected customer; network = authenticated top-level cid. */
export interface AnalyticsViewScope {
  kind: AnalyticsScopeKind;
  cid: number;
}

function base(scope: AnalyticsViewScope): string {
  return `analytics/${scope.kind === 'account' ? 'accounts' : 'networks'}/${scope.cid}`;
}

export class AnalyticsApi {
  static async getMeta(): Promise<AnalyticsMeta> {
    return webApi.get<AnalyticsMeta>('analytics/meta');
  }

  static async getOverview(
    scope: AnalyticsViewScope,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<OverviewData>> {
    return webApi.get(`${base(scope)}/overview`, { from: range.from, to: range.to });
  }

  static async getTopContent(
    scope: AnalyticsViewScope,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<AnalyticsEnvelope<TopContentData>> {
    return webApi.get(`${base(scope)}/top-content`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }

  static async getConcurrency(
    scope: AnalyticsViewScope,
    range: UtcRange,
    interval: AnalyticsInterval
  ): Promise<AnalyticsEnvelope<ConcurrencyData>> {
    return webApi.get(`${base(scope)}/concurrency`, {
      from: range.from,
      to: range.to,
      interval,
    });
  }

  static async getLive(scope: AnalyticsViewScope): Promise<AnalyticsEnvelope<LiveData>> {
    return webApi.get(`${base(scope)}/concurrency/live`);
  }

  /** Account scope only. */
  static async getLiveByMedia(
    cid: number,
    limit?: number
  ): Promise<AnalyticsEnvelope<LiveByMediaData>> {
    return webApi.get(`analytics/accounts/${cid}/concurrency/live/by-media`, { limit });
  }

  /** Account scope only. */
  static async getAudience(
    cid: number,
    dimension: AnalyticsDimension,
    range: UtcRange,
    metric: AnalyticsMetric
  ): Promise<AnalyticsEnvelope<AudienceData>> {
    return webApi.get(`analytics/accounts/${cid}/audience/${dimension}`, {
      from: range.from,
      to: range.to,
      metric,
    });
  }

  /** Account scope only; range capped at 90 days by the control. */
  static async getHeatmap(
    cid: number,
    range: UtcRange
  ): Promise<AnalyticsEnvelope<HeatmapData>> {
    return webApi.get(`analytics/accounts/${cid}/heatmap/concurrency`, {
      from: range.from,
      to: range.to,
    });
  }

  static async getTransfer(
    scope: AnalyticsViewScope,
    range: UtcRange,
    finalizedOnly?: boolean
  ): Promise<AnalyticsEnvelope<TransferData>> {
    return webApi.get(`${base(scope)}/transfer`, {
      from: range.from,
      to: range.to,
      finalizedOnly: finalizedOnly ? true : undefined,
    });
  }

  /** Network scope only. */
  static async getSubaccounts(
    cid: number,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<AnalyticsEnvelope<SubaccountsData>> {
    return webApi.get(`analytics/networks/${cid}/subaccounts`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }

  static async downloadTopContentCsv(
    scope: AnalyticsViewScope,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<BlobResponse> {
    return webApi.getBlob(`${base(scope)}/exports/top-content.csv`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }

  /** Network scope only. */
  static async downloadSubaccountsCsv(
    cid: number,
    range: UtcRange,
    metric: AnalyticsMetric,
    limit?: number
  ): Promise<BlobResponse> {
    return webApi.getBlob(`analytics/networks/${cid}/exports/subaccounts.csv`, {
      from: range.from,
      to: range.to,
      metric,
      limit,
    });
  }
}
